import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cx } from "@/utils/cx";
import { Spinner } from "../spinner";

const buttonVariants = cva(
	cx(
		"inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:bg-disabled disabled:text-fg-disabled aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
		"data-[disabled]:cursor-not-allowed! data-[disabled]:border-disabled data-[disabled]:bg-disabled_subtle data-[disabled]:text-disabled data-[disabled]:shadow-none"
	),
	{
		variants: {
			variant: {
				default:
					"bg-brand-solid text-white shadow-xs hover:bg-brand-solid_hover active:bg-brand-solid active:shadow-button",
				destructive:
					"bg-error-solid text-white hover:bg-[#B42318] active:bg-error-solid active:shadow-button-destructive dark:bg-error-solid",
				outline:
					"pointer-events-auto! border border-primary bg-background text-secondary shadow-xs hover:text-accent-foreground enabled:active:shadow-button enabled:not-[active]:hover:bg-bg-primary_hover disabled:cursor-not-allowed! dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
				secondary:
					"border border-primary bg-primary text-black shadow-xs hover:bg-primary_hover active:bg-primary active:shadow-xs",
				ghost:
					"hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
				link: "font-bold text-brand-secondary underline-offset-4 hover:text-brand-secondary_hover hover:underline",
			},
			size: {
				default: "h-9 px-4 py-2 has-[>svg]:px-3",
				sm: "h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5",
				lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
				icon: "size-9",
				"icon-sm": "size-8",
				"icon-lg": "size-10",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	}
);

function Button({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<"button"> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
	}) {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			className={cx(buttonVariants({ variant, size, className }))}
			data-slot="button"
			{...props}
		/>
	);
}

type ButtonWithSpinnerProps = React.ComponentProps<"button"> &
	React.PropsWithChildren &
	VariantProps<typeof buttonVariants> & {
		isLoading?: boolean;
	};

const ButtonWithSpinner = ({
	isLoading = false,
	className,
	variant,
	size,
	children,
	...props
}: ButtonWithSpinnerProps) => (
	<Button
		className={cx("w-full", buttonVariants({ variant, size, className }))}
		disabled={isLoading}
		type="submit"
		{...props}
	>
		{isLoading ? (
			<>
				<Spinner disabled={isLoading} />
				Enviando...
			</>
		) : (
			children
		)}
	</Button>
);

export { Button, buttonVariants, ButtonWithSpinner };
